import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { setActiveCompany } from '@/domain/companies/service';
import { BuildingIcon, CheckIcon, ChevronDownIcon, PlusIcon, SettingsIcon } from '../icons';
import { ENTITY_LABEL } from '../strings';
import { useCompanies, useCompany } from '../hooks/useCompany';
import { Sheet } from '../components/Sheet';
import { formatOrgNumber } from '../format';

export interface CompanySwitcherProps {
  /** Icon-only trigger (rail). */
  compact?: boolean | undefined;
  /** Text-only trigger for the mobile top bar. */
  inline?: boolean | undefined;
}

/** Shows the active company; opens a sheet to switch or add one. */
export function CompanySwitcher({ compact = false, inline = false }: CompanySwitcherProps) {
  const { company } = useCompany();
  const companies = useCompanies() ?? [];
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const name = company?.name ?? 'Inget företag';

  return (
    <>
      <button
        type="button"
        className={['company-switcher', compact ? 'company-switcher-compact' : '', inline ? 'company-switcher-inline' : ''].filter(Boolean).join(' ')}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={compact ? `Företag: ${name}` : undefined}
        data-tooltip={compact ? name : undefined}
      >
        <span className="company-switcher-icon">
          <BuildingIcon size={20} />
        </span>
        {!compact ? (
          <span className="company-switcher-text">
            <span className="company-switcher-name truncate">{name}</span>
            {!inline && company ? <span className="company-switcher-sub truncate">{ENTITY_LABEL[company.entityType]}</span> : null}
          </span>
        ) : null}
        {!compact ? <ChevronDownIcon size={16} className="company-switcher-caret" /> : null}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Företag">
        <ul className="company-list" role="radiogroup" aria-label="Aktivt företag">
          {companies.map((c) => {
            const active = c.id === company?.id;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={active ? 'company-item company-item-active' : 'company-item'}
                  onClick={() => {
                    void setActiveCompany(c.id).then(() => {
                      setOpen(false);
                      navigate('/hem');
                    });
                  }}
                >
                  <span className="company-item-text">
                    <span className="company-item-name">{c.name}</span>
                    <span className="company-item-sub">
                      {ENTITY_LABEL[c.entityType]}
                      {c.orgNumber ? ` · ${formatOrgNumber(c.orgNumber)}` : ''}
                    </span>
                  </span>
                  {active ? <CheckIcon size={18} className="company-item-check" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="company-actions">
          <Link to="/mer/foretag" className="company-action" onClick={() => setOpen(false)}>
            <SettingsIcon size={18} />
            <span>Företagsinställningar</span>
          </Link>
          <Link to="/start" className="company-action" onClick={() => setOpen(false)}>
            <PlusIcon size={18} />
            <span>Lägg till ett företag eller en förening</span>
          </Link>
        </div>
      </Sheet>
    </>
  );
}
